import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type JobDetail = components['schemas']['JobDetailResponse'];
export type JobNote = components['schemas']['JobNoteResponse'];
export type NewJobNote = components['schemas']['CreateJobNoteRequest'];
export type JobNoteEdits = components['schemas']['UpdateJobNoteRequest'];
export type ChangeJobStatus = components['schemas']['ChangeJobStatusRequest'];

/**
 * The job detail page's data (M1-13): the job itself, the transitions legal
 * from its current status, and its notes thread — one request, per the AC's
 * "notes load with the job".
 *
 * A 403 here is a real, distinguishable answer (a Technician opening a job
 * that is not theirs), not a transport failure — surfaced as
 * `JobNotPermittedError` the same way `CustomerNotFoundError` distinguishes a
 * stale link from a real failure at M1-04. A 404 means either the job does
 * not exist or belongs to another tenant; those are one answer everywhere
 * else in this API, so they stay one answer here too.
 */
export function useJob(jobId: string | undefined) {
  return useQuery({
    queryKey: ['jobs', jobId],
    enabled: jobId !== undefined,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/jobs/{id}', {
        params: { path: { id: jobId! } },
      });
      if (response.status === 403) {
        throw new JobNotPermittedError();
      }
      if (response.status === 404) {
        throw new JobNotFoundError();
      }
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/jobs/{id} failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
  });
}

export class JobNotFoundError extends Error {
  constructor() {
    super('Job not found');
    this.name = 'JobNotFoundError';
  }
}

export class JobNotPermittedError extends Error {
  constructor() {
    super('Not permitted to view this job');
    this.name = 'JobNotPermittedError';
  }
}

/**
 * Job detail mutations: add/edit a note, change status, assign/reassign/
 * unassign. None are optimistic, unlike the dispatch board's drag — the same
 * reasoning `useCustomerActions.ts` gives: these are deliberate edits to a
 * record, not a small frequent reversible gesture, so a spinner on a button
 * pressed once is the honest cost, not a UI worth inventing a value to skip.
 *
 * All four invalidate `['jobs', jobId]`, the single query this page reads —
 * simpler than patching four different shapes of response into the cache by
 * hand, and correct: a re-fetched detail can never disagree with itself.
 */

export function useAddJobNote(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (note: NewJobNote) => {
      const { data, response } = await apiClient.POST('/api/v1/jobs/{jobId}/notes', {
        params: { path: { jobId } },
        body: note,
      });
      if (!response.ok || !data) {
        throw new Error(`POST /api/v1/jobs/{jobId}/notes failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['jobs', jobId] }),
  });
}

export function useEditJobNote(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ noteId, edits }: { noteId: string; edits: JobNoteEdits }) => {
      const { data, response } = await apiClient.PUT('/api/v1/jobs/{jobId}/notes/{noteId}', {
        params: { path: { jobId, noteId } },
        body: edits,
      });
      if (!response.ok || !data) {
        throw new Error(`PUT /api/v1/jobs/{jobId}/notes/{noteId} failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['jobs', jobId] }),
  });
}

/** The API declined the transition and said why — surfaced verbatim, not replaced. */
export class JobStatusRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'JobStatusRefusedError';
  }
}

export function useChangeJobStatus(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (change: ChangeJobStatus) => {
      const { data, error, response } = await apiClient.POST('/api/v1/jobs/{id}/status', {
        params: { path: { id: jobId } },
        body: change,
      });
      // A 409 is the API declining for a stated reason — an illegal transition,
      // or (M1-20) a required form still unsubmitted — not a transport failure.
      // The AC asks that the reason be SHOWN, so it is carried up rather than
      // flattened into a generic "couldn't change status".
      //
      // Read off `error`, never `data`: openapi-fetch only populates `data` for
      // a 2xx, so a `Conflict<string>` with a perfectly good JSON body lands on
      // `error` — and getting that backwards does not throw, it just silently
      // swallows the message (M1-05 paid for this one).
      if (response.status === 409) {
        throw new JobStatusRefusedError(
          typeof error === 'string' ? error : `POST /api/v1/jobs/{id}/status refused: ${response.status}`,
        );
      }
      if (!response.ok || !data) {
        throw new Error(`POST /api/v1/jobs/{id}/status failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['jobs', jobId] });
      // The board's own cache, if it happens to be warm — a status change on
      // the detail page should not leave a stale card on the board.
      void queryClient.invalidateQueries({ queryKey: ['jobs', 'board'] });
    },
  });
}

export function useAssignJobFromDetail(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assignedUserId: string | null) => {
      const { data, response } = await apiClient.POST('/api/v1/jobs/{id}/assign', {
        params: { path: { id: jobId } },
        body: { assignedUserId },
      });
      if (!response.ok || !data) {
        throw new Error(`POST /api/v1/jobs/{id}/assign failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['jobs', jobId] });
      void queryClient.invalidateQueries({ queryKey: ['jobs', 'board'] });
    },
  });
}

/**
 * Set or clear how this job came in (M1-45) — the same shape as the customer's,
 * against the same-shaped route, and its own route for the same reason: a null
 * on a route that does nothing else unambiguously means "clear it".
 *
 * A job's attribution is deliberately separate from its customer's. A repeat
 * customer who called this time from a different ad is exactly the case the
 * field exists to record, so the job's own value wins wherever both exist.
 */
export function useSetJobLeadSource(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (lead: { leadSourceId: string | null; leadSourceDetail: string | null }) => {
      const { data, response } = await apiClient.PUT('/api/v1/jobs/{id}/lead-source', {
        params: { path: { id: jobId } },
        body: lead,
      });
      if (!response.ok || !data) {
        throw new Error(`PUT /api/v1/jobs/{id}/lead-source failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => {
      // This job only: the board and the customer's job list cache the same
      // field and render none of it, and a label RENAME is the catalog's own
      // job to invalidate (useLeadSources.invalidateCatalog).
      void queryClient.invalidateQueries({ queryKey: ['jobs', jobId] });
    },
  });
}
